(() => {
  const ROOT_SELECTOR = '[data-tora-product]';

  const initializeGallery = (root) => {
    const stage = root.querySelector('[data-tora-gallery-stage]');
    const slides = Array.from(root.querySelectorAll('[data-tora-gallery-slide]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-tora-gallery-thumbnail]'));
    const count = root.querySelector('[data-tora-gallery-count]');

    if (!stage || slides.length < 1) return { showMedia: () => {} };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = root.dataset.localeDirection === 'rtl';
    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active')),
    );
    let touchStartX = 0;
    let touchStartY = 0;

    const showSlide = (requestedIndex, focusThumbnail = false) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));

        if (!isActive) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      thumbnails.forEach((thumbnail, index) => {
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.toggleAttribute('aria-current', isActive);

        if (isActive) {
          thumbnail.scrollIntoView({
            behavior: reducedMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
          if (focusThumbnail) thumbnail.focus({ preventScroll: true });
        }
      });

      if (count) count.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    root.querySelectorAll('[data-tora-gallery-direction]').forEach((button) => {
      button.addEventListener('click', () => {
        const movement = button.dataset.toraGalleryDirection === 'next' ? 1 : -1;
        showSlide(activeIndex + movement);
      });
    });

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        showSlide(Number(thumbnail.dataset.index));
      });
    });

    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showSlide(activeIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showSlide(activeIndex - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        showSlide(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        showSlide(slides.length - 1, true);
      }
    });

    stage.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      },
      { passive: true },
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;

        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

        const isNext = isRtl ? deltaX > 0 : deltaX < 0;
        showSlide(activeIndex + (isNext ? 1 : -1));
      },
      { passive: true },
    );

    showSlide(activeIndex);

    return {
      showMedia(mediaId) {
        if (!mediaId) return;
        const targetIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (targetIndex >= 0) showSlide(targetIndex);
      },
    };
  };

  const initializeVariantPicker = (root, gallery) => {
    const cards = Array.from(root.querySelectorAll('[data-tora-variant-card]'));
    const price = root.querySelector('[data-tora-price-current]');
    const compare = root.querySelector('[data-tora-price-compare]');
    const saving = root.querySelector('[data-tora-price-saving]');
    const perCup = root.querySelector('[data-tora-per-cup]');
    const availability = root.querySelector('[data-tora-availability]');
    const availabilityIcon = availability?.querySelector(':scope > span:first-child');
    const availabilityTitle = root.querySelector('[data-tora-availability-title]');
    const mainSubmit = root.querySelector('[data-tora-main-submit]');
    const submitContent = root.querySelector('[data-tora-submit-content]');
    const submitLabel = root.querySelector('[data-tora-submit-label]');
    const submitPrice = root.querySelector('[data-tora-submit-price]');
    const soldOutMessage = mainSubmit?.querySelector('.sold-out-message');
    const stickyVariant = root.querySelector('[data-tora-sticky-variant]');
    const stickyPrice = root.querySelector('[data-tora-sticky-price]');
    const stickySubmit = root.querySelector('[data-tora-sticky-submit]');
    const stickyLabel = root.querySelector('[data-tora-sticky-label]');
    const productForm = root.querySelector('[data-tora-product-form] form');
    const isArabic = root.dataset.localeDirection === 'rtl';

    if (cards.length < 1 || !mainSubmit || !productForm) return;

    const labels = {
      add: isArabic ? 'إضافة إلى السلة' : 'Add to cart',
      available: isArabic ? 'متوفرة الآن' : 'Available now',
      soldOut: isArabic ? 'نفدت الكمية' : 'Sold out',
      delivery: root.dataset.deliveryMessage,
    };

    const updateVariant = (card, updateUrl = true) => {
      if (!card) return;

      const input = card.querySelector('input[name="id"]');
      const isAvailable = card.dataset.available === 'true';
      const compareText = card.dataset.compare || '';
      const savingText = card.dataset.saving || '';

      cards.forEach((variantCard) => {
        variantCard.classList.toggle('is-selected', variantCard === card);
      });

      if (input && !input.checked) input.checked = true;
      if (price) price.textContent = card.dataset.price || '';

      if (compare) {
        compare.textContent = compareText;
        compare.hidden = !compareText;
      }

      if (saving) {
        saving.textContent = savingText;
        saving.hidden = !savingText;
      }

      if (perCup) perCup.textContent = card.dataset.perCup || '';

      if (availability) availability.classList.toggle('is-unavailable', !isAvailable);
      if (availabilityIcon) availabilityIcon.textContent = isAvailable ? '✓' : '×';
      if (availabilityTitle) availabilityTitle.textContent = isAvailable ? labels.available : labels.soldOut;
      const availabilityBody = root.querySelector('[data-tora-availability-body]');
      if (availabilityBody) availabilityBody.textContent = labels.delivery;

      mainSubmit.disabled = !isAvailable;
      mainSubmit.removeAttribute('aria-disabled');
      if (submitContent) submitContent.classList.remove('hidden');
      if (soldOutMessage) soldOutMessage.classList.add('hidden');
      if (submitLabel) submitLabel.textContent = isAvailable ? labels.add : labels.soldOut;
      if (submitPrice) submitPrice.textContent = card.dataset.price || '';

      if (stickyVariant) stickyVariant.textContent = card.dataset.variantTitle || '';
      if (stickyPrice) stickyPrice.textContent = card.dataset.price || '';
      if (stickySubmit) stickySubmit.disabled = !isAvailable;
      if (stickyLabel) stickyLabel.textContent = isAvailable ? labels.add : labels.soldOut;

      gallery.showMedia(card.dataset.mediaId);

      if (updateUrl && input?.value) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url.toString());
      }
    };

    cards.forEach((card) => {
      const input = card.querySelector('input[name="id"]');
      input?.addEventListener('change', () => updateVariant(card));
    });

    stickySubmit?.addEventListener('click', () => {
      if (stickySubmit.disabled || mainSubmit.disabled) return;
      productForm.requestSubmit(mainSubmit);
    });

    const initiallyChecked = cards.find((card) => card.querySelector('input[name="id"]')?.checked) || cards[0];
    updateVariant(initiallyChecked, false);
  };

  const initializeRoot = (root) => {
    if (root.dataset.toraProductInitialized === 'true') return;
    root.dataset.toraProductInitialized = 'true';

    const gallery = initializeGallery(root);
    initializeVariantPicker(root, gallery);
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
